/**
 * Helpers for controlled numeric <input> fields.
 *
 * React does not overwrite the DOM value of an `<input type="number">` when the
 * parsed state matches what it already rendered, so a field showing "0" that
 * the user types into becomes "02", then "022" — the arithmetic stays correct
 * (parseFloat ignores the leading zero) but the display never recovers.
 *
 * The fix is to hold the raw string the user typed and strip redundant leading
 * zeros before it is stored, so the value React renders differs from the DOM
 * value and React writes the corrected string back.
 */

/**
 * Normalises a raw numeric input string for display.
 *
 * Preserves the states a user passes through while typing — "" (cleared),
 * "-", "2." — and only removes zeros that are followed by another digit:
 *
 *   "02"   -> "2"       "0"    -> "0"
 *   "007"  -> "7"       "00"   -> "0"
 *   "0.5"  -> "0.5"     "-01"  -> "-1"
 *   ""     -> ""        "2."   -> "2."
 */
export const normalizeNumericInput = (raw) => {
  if (raw === null || raw === undefined) return '';

  const value = String(raw);
  if (value === '') return '';

  const sign = value.startsWith('-') ? '-' : '';
  const unsigned = sign ? value.slice(1) : value;

  // Strip leading zeros only when a digit follows, so "0.5" and a lone "0" survive.
  const stripped = unsigned.replace(/^0+(?=\d)/, '');

  return sign + stripped;
};

/** Reads a normalised numeric string as a number, treating blanks as 0. */
export const toNumber = (value) => parseFloat(value) || 0;
